import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ExternalLink } from 'lucide-react';
import {
  RosterPageV2Props,
  RosterPageV2State
} from '../../types';
import { v1Api } from '../../services/api';
import Button from '../common/Button';
import Skeleton from '../common/Skeleton';

// Components
import WeekSelector from './WeekSelector';
import ViewToggle from './ViewToggle';
import WeeklyRosterGrid from './WeeklyRosterGrid';
import StartSitImpactPanel from './StartSitImpactPanel';
import SeasonOverview from './SeasonOverview';

/**
 * RosterPageV2 - Complete roster management interface
 * Features:
 * - Week-by-week roster snapshots
 * - Start/sit analysis and missed opportunities
 * - Transaction history with filtering
 * - Season overview with draft and evolution
 * - Responsive layout with right sidebar
 */
const RosterPageV2: React.FC<RosterPageV2Props> = ({
  teamId,
  initialWeek = 14, // Default to a reasonable week
  onViewDraftBoard
}) => {
  const [state, setState] = useState<RosterPageV2State>({
    selectedWeek: initialWeek,
    viewMode: 'week',
    weeklyRoster: null,
    seasonOverview: null,
    startSitAnalysis: null,
    transactions: null,
    loading: true,
    error: null,
    completedWeeks: [],
    isByeWeek: false
  });

  const [rosterSortBy, setRosterSortBy] = useState<'name' | 'points' | 'position' | 'status'>('status');
  const navigate = useNavigate();
  const { espnLeagueId } = useParams<{ espnLeagueId: string }>();

  // Determine completed weeks (regular season + playoff games)
  // Weeks 1-14: Regular season, Week 15: Bye, Week 16-17: Playoff games, Week 18: Bye
  const completedWeeks = useMemo(() => [
    ...Array.from({ length: 14 }, (_, i) => i + 1), // Weeks 1-14
    16, 17 // Playoff weeks with actual games
  ], []);

  // Load data for the current view mode and week
  const loadData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null, isByeWeek: false }));

    try {
      if (state.viewMode === 'week') {
        // Load week-specific data
        const [weeklyRoster, startSitAnalysis] = await Promise.all([
          v1Api.teams.getWeeklyRoster(teamId, state.selectedWeek),
          v1Api.teams.getStartSitAnalysis(teamId, state.selectedWeek)
        ]);

        setState(prev => ({
          ...prev,
          weeklyRoster,
          startSitAnalysis,
          loading: false,
          completedWeeks
        }));
      } else {
        // Load season overview data
        const seasonOverview = await v1Api.teams.getSeasonOverview(teamId);
        
        setState(prev => ({
          ...prev,
          seasonOverview,
          loading: false,
          completedWeeks
        }));
      }
    } catch (error: any) {
      console.error('Error loading roster data:', error);
      
      // Check if this is a 404 error indicating no roster data (bye week)
      const isNoDataError = error?.response?.status === 404 && 
                            error?.response?.data?.detail?.includes('No roster data found');
      
      setState(prev => ({
        ...prev,
        error: isNoDataError ? null : 'Failed to load roster data. Please try again.',
        isByeWeek: isNoDataError,
        loading: false
      }));
    }
  }, [teamId, state.selectedWeek, state.viewMode, completedWeeks]);

  // Load data when component mounts or key dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleWeekChange = (week: number) => {
    setState(prev => ({ ...prev, selectedWeek: week }));
  };

  const handleViewChange = (viewMode: 'week' | 'season') => {
    setState(prev => ({ ...prev, viewMode }));
  };

  const handlePlayerClick = (playerId: number) => {
    navigate(`/leagues/espn/${espnLeagueId}/players/${playerId}`);
  };

  const handleWeekClickFromOverview = (week: number) => {
    setState(prev => ({ 
      ...prev, 
      selectedWeek: week, 
      viewMode: 'week' 
    }));
  };

  if (state.loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>
        
        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-300 mb-2">
            Error Loading Roster
          </h3>
          <p className="text-slate-400 mb-4">{state.error}</p>
          <Button onClick={loadData} className="mr-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Bye week handling
  if (state.isByeWeek && state.viewMode === 'week') {
    return (
      <div className="space-y-6">
        {/* Header Controls */}
        <div className="flex items-center justify-between">
          <WeekSelector
            selectedWeek={state.selectedWeek}
            maxWeek={18}
            onWeekChange={handleWeekChange}
            completedWeeks={completedWeeks}
          />
          
          <div className="flex items-center space-x-4">
            <ViewToggle
              activeView={state.viewMode}
              onViewChange={handleViewChange}
            />
            
            {onViewDraftBoard && (
              <Button
                variant="secondary"
                onClick={onViewDraftBoard}
                className="flex items-center space-x-2"
              >
                <span>View Draft Board</span>
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Bye Week Message */}
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏖️</span>
            </div>
            <h3 className="text-xl font-semibold text-slate-300 mb-2">
              Bye Week
            </h3>
            <p className="text-slate-400">
              This team had a bye in Week {state.selectedWeek}. No roster data available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <WeekSelector
          selectedWeek={state.selectedWeek}
          maxWeek={18}
          onWeekChange={handleWeekChange}
          completedWeeks={completedWeeks}
        />
        
        <div className="flex items-center space-x-4">
          <ViewToggle
            activeView={state.viewMode}
            onViewChange={handleViewChange}
          />
          
          {onViewDraftBoard && (
            <Button
              variant="secondary"
              onClick={onViewDraftBoard}
              className="flex items-center space-x-2"
            >
              <span>View Draft Board</span>
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      {state.viewMode === 'week' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Roster Grid */}
          <div className="lg:col-span-2">
            {state.weeklyRoster && (
              <WeeklyRosterGrid
                rosterData={state.weeklyRoster}
                onPlayerClick={handlePlayerClick}
                sortBy={rosterSortBy}
                onSortChange={setRosterSortBy}
              />
            )}
          </div>

          {/* Right Column: Analysis Panels */}
          <div className="space-y-6">
            {/* Start/Sit Impact Panel */}
            {state.startSitAnalysis && (
              <StartSitImpactPanel
                analysis={state.startSitAnalysis}
                matchup={state.weeklyRoster?.matchup}
                onPlayerClick={handlePlayerClick}
              />
            )}

          </div>
        </div>
      ) : (
        // Season Overview
        <div>
          {state.seasonOverview && (
            <SeasonOverview
              data={state.seasonOverview}
              onDraftPlayerClick={handlePlayerClick}
              onWeekClick={handleWeekClickFromOverview}
            />
          )}
        </div>
      )}

    </div>
  );
};

export default RosterPageV2;
